import { updateCategory, useCategory, type CategoryForm as CategoryFormValues } from '@gozoki/api'
import type { Category } from '@gozoki/api-types'
import { useMutation } from '@tanstack/react-query'
import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { toast } from 'sonner'
import z from 'zod/v3'
import { CategoryForm } from '~/components/routes/categories/CategoryForm'
import { Spinner } from '~/components/ui/spinner'
import { getAxiosErrorMessage } from '~/lib/utils'
import { useRights } from '~/utils/hooks'
import { withRoutePermissions } from '~/utils/rbac'

export const Route = createFileRoute('/_authenticated/categories/$categoryId/edit')({
  staticData: {
    permission: ['read', 'categories'],
  },
  params: {
    parse: z.object({ categoryId: z.coerce.number() }).parse,
  },
  component: EditCategoryPage,
})
withRoutePermissions(Route)

const toFormValues = (category: Category): CategoryFormValues => ({
  name: category.name,
  image: null,
  categoryId: category.categoryId,
  description: category.description ?? '',
  rank: category.rank.toString(),
})

function EditCategoryPage() {
  const { categoryId } = Route.useParams()
  const navigate = useNavigate()
  const isUserAdmin = useRights(['ADMIN'])

  const { data: category, isLoading } = useCategory(categoryId)
  const { mutateAsync: update } = useMutation({ mutationFn: updateCategory })

  const navigateBack = () => navigate({ to: '/categories/categories' })

  if (isLoading) {
    return (
      <div className="flex justify-center p-16">
        <Spinner />
      </div>
    )
  }

  if (!category) {
    return <div className="text-muted-foreground p-16 text-center">Catégorie introuvable.</div>
  }

  return (
    <CategoryForm
      key={category.id}
      title={isUserAdmin ? 'Modifier une catégorie' : 'Voir une catégorie'}
      submitLabel="Modifier"
      canEdit={isUserAdmin}
      imageDefaultName={category.image?.name}
      defaultValues={toFormValues(category)}
      onCancel={navigateBack}
      onSubmit={async (values) => {
        try {
          await update({ id: categoryId, data: values })
          navigateBack()
        } catch (error) {
          toast.error(getAxiosErrorMessage(error))
        }
      }}
    />
  )
}
